import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HardHat, ArrowRight, Mail, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <SEO title="Reset Your Password" description="Request a password reset link for your ContractorsLab account." path="/forgot-password" noindex />

      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
            <HardHat className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-black text-slate-900 tracking-tight">CONTRACTORSLAB</span>
        </Link>

        <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-8" data-testid="forgot-password-card">
          {!sent ? (
            <>
              <h1 className="text-2xl font-black text-slate-900 mb-2">FORGOT YOUR PASSWORD?</h1>
              <p className="text-slate-600 mb-6 text-sm">
                Enter the email associated with your account. We'll send you a secure link to reset it.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6 text-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                    data-testid="forgot-password-email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide py-4 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? 'Sending…' : 'Send Reset Link'}
                  {!loading && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-password-sent">
              <div className="w-14 h-14 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-orange-600" />
              </div>
              <h1 className="text-2xl font-black text-slate-900 mb-2">CHECK YOUR INBOX</h1>
              <p className="text-slate-600 text-sm leading-relaxed">
                If an account exists for <strong className="text-slate-900">{email}</strong>, a password reset link is on its way.
                The link expires in <strong>60 minutes</strong>.
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Didn't get it? Check your spam folder or try again in a few minutes.
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/auth" className="text-slate-600 hover:text-orange-600 text-sm" data-testid="back-to-login-link">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
